import { useEffect, useMemo, useRef, useState } from 'react'
import useFeaturePermissions from '../../../hooks/useFeaturePermissions'
// Quote Context
import { useQuoteContext } from '../../../hooks/useQuoteContext'
import useCoTermMonths, { MONTH_TO_MONTH } from '../../../hooks/useCoTermMonths'
// Bootstrap Imports
import { InputGroup, Form, Modal, Button } from 'react-bootstrap'
import { TermLengthType } from '../../../types/types'
import toast from 'react-hot-toast'
//@ts-ignore
import {
   isProductOutsideTermRange,
   formatTermRange,
   //@ts-ignore
} from '../../../js/validate.jsx'

const CO_TERM_SENTINEL = -1

/**
 * Finds the closest available term_length option that is > 1 to the
 * original contract term. Prefers exact match, then closest greater,
 * then closest lesser (but still > 1).
 */
function findClosestTerm(
   terms: TermLengthType[],
   contractTerm: number,
): TermLengthType | null {
   const candidates = terms.filter((t) => t.term_length > MONTH_TO_MONTH)
   if (candidates.length === 0) return null

   // Exact match
   const exact = candidates.find((t) => t.term_length === contractTerm)
   if (exact) return exact

   // Sort by distance, preferring greater values on tie
   candidates.sort((a, b) => {
      const distA = Math.abs(a.term_length - contractTerm)
      const distB = Math.abs(b.term_length - contractTerm)
      if (distA !== distB) return distA - distB
      return b.term_length - a.term_length // prefer greater
   })
   return candidates[0]
}

export default function TermSelect() {
   const state = useQuoteContext()
   const { getString, termLengthsList, user_view } = state
   const { hasPermission } = useFeaturePermissions()

   //TODO: EASYBUTTON Remove the TermContract feature permission once we go live
   if (!hasPermission('TermContractFeature', state.user_role)) {
      return null
   }

   // Hides the component if user does not have roles and the term length is 1.
   if (
      !hasPermission('InternalToolbarTermSelect', state.user_role) &&
      state.termLength === 1
   ) {
      return null
   }

   // Centralized co-term calculation shared with PrepaySelect
   const {
      coTermMonths,
      hasActiveContract,
      activationPastContract,
      contractTerm,
   } = useCoTermMonths()

   // Order terms: recommended (closest to original contract) first, then
   // others by display_order, month-to-month last.
   const sortedTerms: TermLengthType[] = useMemo(() => {
      if (!termLengthsList) return []
      const terms = [...termLengthsList]
      const recommended =
         hasActiveContract && contractTerm
            ? findClosestTerm(terms, contractTerm)
            : null

      // Separate month-to-month, recommended, and the rest
      const monthToMonth = terms.filter((t) => t.term_length === MONTH_TO_MONTH)
      const rest = terms
         .filter(
            (t) =>
               t.term_length !== MONTH_TO_MONTH &&
               t.id !== (recommended?.id ?? -999),
         )
         .sort((a, b) => a.display_order - b.display_order)

      // Recommended first, then other terms, then co-term is handled in JSX, then month-to-month
      const ordered: TermLengthType[] = []
      if (recommended) ordered.push(recommended)
      ordered.push(...rest)
      ordered.push(...monthToMonth)
      return ordered
   }, [termLengthsList, hasActiveContract, contractTerm])

   // Auto-select the recommended term on first load when a contract exists
   const hasAutoSelected = useRef(false)
   useEffect(() => {
      if (
         hasAutoSelected.current ||
         !hasActiveContract ||
         !contractTerm ||
         !termLengthsList ||
         state.isCoTerm
      )
         return
      // Only auto-select if current value is month-to-month (default)
      if (state.termLength !== MONTH_TO_MONTH) return
      const recommended = findClosestTerm(termLengthsList, contractTerm)
      if (recommended) {
         state.setTermLength(recommended.term_length)
         hasAutoSelected.current = true
      }
   }, [hasActiveContract, contractTerm, termLengthsList])

   // Pending term change awaiting user confirmation when products would be removed.
   const [pendingChange, setPendingChange] = useState<{
      val: number
      isCoTerm: boolean
      effectiveTerm: number
      affected: Array<{
         productCode: string
         productName: string
         message: string
      }>
   } | null>(null)

   /**
    * Build the list of currently-selected product codes (value > 0) across both
    * the dynamic-products map and the hardcoded counters/booleans/services.
    * Mirrors the selectedProducts map used in shouldDisableDiscount().
    */
   const collectSelectedProductCodes = (): string[] => {
      const selected = new Set<string>()
      state.dynamicProducts?.forEach((value, productCode) => {
         if (value > 0) selected.add(productCode)
      })
      if (state.productAdditionalJobsCount > 0) selected.add('addJobPosting')
      if (state.productAdditionalStorageCount > 0) selected.add('addStorage')
      if (state.productBenefitsAdministrationEECount > 0)
         selected.add('benAdmin')
      if (state.benAdminImpType !== 'none') selected.add('benAdminImp')
      if (
         state.coreProductSelected !== 'core0' &&
         state.productCoreEECount > 0
      ) {
         selected.add(state.coreProductSelected)
         if (state.impType === 'full')
            selected.add(state.coreProductSelected + 'Imp')
         if (state.impType === 'quickstart')
            selected.add(state.coreProductSelected + 'Quickstart')
      }
      if (state.productHistoricalDataImport)
         selected.add('historicalDataImport')
      if (state.productMarketplaceListing) selected.add('marketplaceListing')
      if (state.productPaidServiceAmount > 0) selected.add('paidService')
      if (state.productPayrollTotalEECount > 0) selected.add('payroll')
      if (
         state.payrollImpType === 'full' ||
         state.productPayrollEINImpTypes?.includes?.('full')
      )
         selected.add('payrollImp')
      if (
         state.payrollImpType === 'foundations' ||
         state.productPayrollEINImpTypes?.includes?.('foundations')
      )
         selected.add('payrollImpFoundations')
      if (state.productPerformanceEECount > 0) selected.add('performanceMan')
      if (state.productSponsorship) selected.add('sponsorship')
      if (state.productTimeTrackingEECount > 0) selected.add('timeTracking')
      if (state.productTestAccount) selected.add('testAccount')
      state.consultingServiceDetails?.forEach((s: any) => {
         if (s.count > 0) selected.add(s.serviceCode)
      })
      state.professionalServiceDetails?.forEach((s: any) => {
         if (s.count > 0) selected.add(s.serviceCode)
      })
      return Array.from(selected)
   }

   const findAffectedProducts = (newTerm: number) => {
      const affected: Array<{
         productCode: string
         productName: string
         message: string
      }> = []
      const selected = collectSelectedProductCodes()
      for (const productCode of selected) {
         const product = state.product_list?.[productCode]
         if (!product) continue
         if (isProductOutsideTermRange(product, newTerm)) {
            affected.push({
               productCode,
               productName: product.ProductName || productCode,
               message: formatTermRange(product),
            })
         }
      }
      return affected
   }

   const applyTermChange = (val: number, isCoTerm: boolean) => {
      if (isCoTerm) {
         state.setIsCoTerm(true)
         state.setTermLength(coTermMonths!)
         if (user_view !== 'External') {
            toast.custom(
               (t) => {
                  const dismiss = () => toast.dismiss(t.id)
                  const handler = (e: MouseEvent) => {
                     const el = document.getElementById(`toast-${t.id}`)
                     if (el && !el.contains(e.target as Node)) {
                        dismiss()
                        document.removeEventListener('mousedown', handler)
                     }
                  }
                  setTimeout(
                     () => document.addEventListener('mousedown', handler),
                     0,
                  )
                  return (
                     <div
                        id={`toast-${t.id}`}
                        style={{
                           background: '#fff',
                           border: '1px solid #e2e2e2',
                           borderRadius: '8px',
                           padding: '12px 16px',
                           boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                           display: 'flex',
                           alignItems: 'flex-start',
                           gap: '10px',
                           maxWidth: '400px',
                           opacity: t.visible ? 1 : 0,
                           transition: 'opacity 0.2s',
                        }}
                     >
                        <span style={{ fontSize: '18px', lineHeight: '1.4' }}>
                           ⚠️
                        </span>
                        <span
                           style={{
                              flex: 1,
                              fontSize: '14px',
                              lineHeight: '1.4',
                           }}
                        >
                           {getString(
                              'product.DifferenceQuote.CoTermWarning.Text',
                           )}
                        </span>
                        <button
                           onClick={dismiss}
                           style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '18px',
                              lineHeight: '1',
                              color: '#999',
                              padding: '0 0 0 4px',
                           }}
                           aria-label='Dismiss'
                        >
                           ✕
                        </button>
                     </div>
                  )
               },
               { duration: 10000 },
            )
         }
      } else {
         state.setIsCoTerm(false)
         state.setTermLength(val)
      }
   }

   const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const val = Number(e.target.value)
      const isCoTerm = val === CO_TERM_SENTINEL
      const effectiveTerm = isCoTerm ? coTermMonths! : val
      const affected = findAffectedProducts(effectiveTerm)
      if (affected.length > 0) {
         setPendingChange({ val, isCoTerm, effectiveTerm, affected })
         return
      }
      applyTermChange(val, isCoTerm)
   }

   const removeProductByCode = (productCode: string) => {
      // Dynamic products: zero the entry in the dynamicProducts map.
      if (state.dynamicProducts?.has(productCode)) {
         state.setDynamicProduct(productCode, 0)
      }
      // Hardcoded products: each has its own setter / change handler.
      switch (productCode) {
         case 'addJobPosting':
            state.onProductAdditionalJobsCountChange?.(0)
            break
         case 'addStorage':
            state.onProductAdditionalStorageCountChange?.(0)
            break
         case 'benAdmin':
            state.onProductBenefitsAdministrationEECountChange?.(0)
            break
         case 'benAdminImp':
            state.setBenAdminImpType?.('none')
            break
         case 'core1':
         case 'core2':
         case 'core3':
            state.setProductCoreEECount?.(0)
            break
         case 'core1Imp':
         case 'core2Imp':
         case 'core3Imp':
         case 'core1Quickstart':
         case 'core2Quickstart':
         case 'core3Quickstart':
            state.setImpType?.('none')
            break
         case 'historicalDataImport':
            state.setProductHistoricalDataImport?.(false)
            break
         case 'marketplaceListing':
            state.setMarketplaceListing?.(false)
            break
         case 'paidService':
            state.onProductPaidServiceAmountChange?.(0)
            break
         case 'payroll':
            state.onProductPayrollEECountChange?.(0)
            break
         case 'payrollImp':
         case 'payrollImpFoundations':
            state.setPayrollImpType?.('none')
            break
         case 'performanceMan':
            state.onProductPerformanceEECountChange?.(0)
            break
         case 'sponsorship':
            state.setProductSponsorship?.(false)
            break
         case 'timeTracking':
            state.onProductTimeTrackingEECountChange?.(0)
            break
         case 'testAccount':
            state.setProductTestAccount?.(false)
            break
         default:
            // Consulting / Professional services: zero the matching entry.
            const consulting = state.consultingServiceDetails?.find(
               (s: any) => s.serviceCode === productCode,
            )
            if (consulting) {
               state.setConsultingServiceDetails?.((prev: any[]) =>
                  prev.map((s: any) =>
                     s.serviceCode === productCode ? { ...s, count: 0 } : s,
                  ),
               )
               break
            }
            const professional = state.professionalServiceDetails?.find(
               (s: any) => s.serviceCode === productCode,
            )
            if (professional) {
               state.setProfessionalServiceDetails?.((prev: any[]) =>
                  prev.map((s: any) =>
                     s.serviceCode === productCode ? { ...s, count: 0 } : s,
                  ),
               )
            }
            break
      }
   }

   const confirmPendingChange = () => {
      if (!pendingChange) return
      pendingChange.affected.forEach(({ productCode }) => {
         removeProductByCode(productCode)
      })
      applyTermChange(pendingChange.val, pendingChange.isCoTerm)
      setPendingChange(null)
   }

   const cancelPendingChange = () => setPendingChange(null)

   const selectValue = state.isCoTerm ? CO_TERM_SENTINEL : state.termLength

   return (
      <>
         <InputGroup className='justify-content-center' size='sm'>
            <InputGroup.Text>
               {getString('product.TermSelect.InputText')}
            </InputGroup.Text>
            <Form.Select size='sm' value={selectValue} onChange={handleChange}>
               {sortedTerms.map((term: TermLengthType) => {
                  const isMonthToMonth = term.term_length === MONTH_TO_MONTH
                  const disabled = isMonthToMonth && !activationPastContract
                  return (
                     <option
                        key={term.id}
                        value={term.term_length}
                        disabled={disabled}
                        style={disabled ? { color: '#999' } : undefined}
                     >
                        {term.name}
                     </option>
                  )
               })}
               {coTermMonths != null && (
                  <option key='coterm' value={CO_TERM_SENTINEL}>
                     {coTermMonths} Month Co-Term
                  </option>
               )}
            </Form.Select>
         </InputGroup>
         <Modal
            show={pendingChange !== null}
            onHide={cancelPendingChange}
            backdrop='static'
            centered
         >
            <Modal.Header closeButton>
               <Modal.Title>Products will be removed</Modal.Title>
            </Modal.Header>
            <Modal.Body>
               <p>
                  Changing the term length to{' '}
                  <strong>
                     {pendingChange?.isCoTerm
                        ? `${pendingChange.effectiveTerm} Month Co-Term`
                        : `${pendingChange?.effectiveTerm} months`}
                  </strong>{' '}
                  will remove the following product
                  {pendingChange && pendingChange.affected.length > 1
                     ? 's'
                     : ''}{' '}
                  from the quote:
               </p>
               <ul>
                  {pendingChange?.affected.map((p) => (
                     <li key={p.productCode}>{p.message}</li>
                  ))}
               </ul>
            </Modal.Body>
            <Modal.Footer>
               <Button variant='secondary' onClick={cancelPendingChange}>
                  Cancel
               </Button>
               <Button variant='primary' onClick={confirmPendingChange}>
                  Confirm and remove
               </Button>
            </Modal.Footer>
         </Modal>
      </>
   )
}
